import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../context/AuthContext';
import { useAuthModal } from '../context/AuthModalContext';
import { toast } from 'sonner';
import {
  AlertCircle,
  Check,
  Clock,
  CreditCard,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  MapPin,
  Package,
  PackageCheck,
  Truck,
  XCircle,
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  formatStatusLabel,
  getEffectiveTimelineKey,
  getShipmentStatusMessage,
  getTimelineSteps,
  TIMELINE_LABELS,
} from '../utils/orderTimeline';

const rupee = (value) => `Rs. ${Number(value || 0).toLocaleString('en-IN')}`;

const getProductImage = (item) => {
  // If the item has color_images and colors, use the same logic as home page
  if (item.color_images && item.colors?.length > 0) {
    const firstColor = item.colors[0];
    
    // Try exact match first
    if (item.color_images[firstColor]?.[0]) {
      return item.color_images[firstColor][0];
    }
    
    // Try case-insensitive match
    const normalizedSearch = firstColor.trim().toLowerCase();
    const matchingKey = Object.keys(item.color_images).find(
      key => key.trim().toLowerCase() === normalizedSearch
    );
    
    if (matchingKey && item.color_images[matchingKey]?.[0]) {
      return item.color_images[matchingKey][0];
    }
    
    // If no match for first color, try any available color
    const firstAvailableColorImages = Object.values(item.color_images).find(images => images?.length > 0);
    if (firstAvailableColorImages?.[0]) {
      return firstAvailableColorImages[0];
    }
  }
  
  // Fallback to regular images
  if (Array.isArray(item.images) && item.images.length > 0) return item.images[0];
  if (typeof item.image === "string") return item.image;
  return "/placeholder.svg";
};

const formatDate = (value, options = {}) => {
  if (!value) return 'Not available';
  return new Date(value).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  });
};

const STATUS_META = {
  placed: { label: 'Placed', Icon: Package, message: 'Your order has been placed successfully.' },
  awaiting_confirmation: { label: 'Placed', Icon: Package, message: 'Your order is waiting for confirmation.' },
  confirmed: { label: 'Confirmed', Icon: PackageCheck, message: 'Your order has been confirmed.' },
  in_process: { label: 'Processing', Icon: Clock, message: 'Your order is being prepared for pickup.' },
  dispatched: { label: 'Dispatched', Icon: Truck, message: 'Your order has been dispatched.' },
  out_for_delivery: { label: 'Out for Delivery', Icon: Truck, message: 'Your order is out for delivery.' },
  delivered: { label: 'Delivered', Icon: Check, message: 'Your order has been delivered successfully.' },
  rto_initiated: { label: 'RTO Initiated', Icon: AlertCircle, message: 'Return to origin has been initiated.' },
  returned: { label: 'Returned', Icon: XCircle, message: 'Your order has been returned.' },
  cancelled: { label: 'Cancelled', Icon: XCircle, message: 'This order has been cancelled.' },
};

const getStatusMeta = (order) => {
  const key = getEffectiveTimelineKey(order);
  const meta = STATUS_META[order?.order_status === 'cancelled' ? 'cancelled' : key] || STATUS_META.placed;

  return {
    key,
    ...meta,
    message: order?.order_status === 'cancelled' ? meta.message : getShipmentStatusMessage(order) || meta.message,
  };
};

const formatAddress = (address) => {
  if (!address) return 'Not available';

  return [
    address.building_name,
    address.building_no,
    address.apt_no ? `Apt ${address.apt_no}` : null,
    address.floor ? `Floor ${address.floor}` : null,
    address.flat_no ? `Flat ${address.flat_no}` : null,
    address.address_line1 || address.street,
    address.address_line2 || address.landmark,
    address.district || null,
    [address.city, address.state].filter(Boolean).join(', '),
    address.country || null,
    address.pincode,
  ]
    .filter(Boolean)
    .join(', ');
};

export default function Orders() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { openAuth } = useAuthModal();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [hiddenAmounts, setHiddenAmounts] = useState({});

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/"); // Redirect to home without showing popup
      return;
    }
    fetchOrders();
  }, [isAuthenticated, navigate]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const cancelOrder = async (orderId) => {
    try {
      await api.post(`/orders/${orderId}/cancel`);
      toast.success('Order cancelled successfully');
      fetchOrders();
    } catch (error) {
      console.error('Failed to cancel order:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel order');
    }
  };

  const canCustomerCancelOrder = (status) => ['placed', 'awaiting_confirmation'].includes(status);

  const toggleAmount = (orderId) => {
    setHiddenAmounts((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const generatePDF = async (order) => {
    try {
      const doc = new jsPDF();

      doc.setFontSize(24);
      doc.setTextColor(255, 87, 34);
      doc.text('ORDER INVOICE', 105, 20, { align: 'center' });

      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text(`Order ID: ${order.id.substring(0, 8).toUpperCase()}`, 20, 40);
      doc.text(`Date: ${new Date(order.created_at).toLocaleDateString('en-IN')}`, 20, 47);
      doc.text(`Status: ${order.order_status.toUpperCase()}`, 20, 54);

      if (order.shipping_address) {
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Shipping Address:', 20, 70);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        doc.text(formatAddress(order.shipping_address), 20, 77, { maxWidth: 170 });
      }

      const tableData = [];
      (order.items || []).forEach((item) => {
        if (item.item_type === 'combo' && item.selected_children && item.selected_children.length > 0) {
          tableData.push([
            item.name,
            item.quantity,
            `Rs. ${parseFloat(item.price).toFixed(2)}`,
            `Rs. ${(parseFloat(item.price) * item.quantity).toFixed(2)}`,
          ]);
          item.selected_children.forEach((child) => {
            tableData.push([`  - ${child.name}`, child.quantity, '-', '-']);
          });
        } else {
          tableData.push([
            item.name,
            item.quantity,
            `Rs. ${parseFloat(item.price).toFixed(2)}`,
            `Rs. ${(parseFloat(item.price) * item.quantity).toFixed(2)}`,
          ]);
        }
      });

      autoTable(doc, {
        startY: 95,
        head: [['Item', 'Qty', 'Price', 'Total']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [255, 87, 34], textColor: [255, 255, 255] },
        styles: { fontSize: 10 },
      });

      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(`Total Amount: Rs. ${parseFloat(order.total_amount).toFixed(2)}`, 20, finalY);

      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Thank you for your order!', 105, finalY + 20, { align: 'center' });

      doc.save(`invoice_${order.id.substring(0, 8)}.pdf`);
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030804] pt-16 pb-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="h-10 bg-[#0a1a0f] rounded-lg w-48 mb-8 animate-pulse" />
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.15 }}
                className="bg-[#0a1a0f] border border-[#1a4d2e]/30 rounded-xl p-6 h-32"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#030804] via-[#050f0a] to-[#030804] pt-16 pb-16 font-[Arial]">
        <div className="max-w-6xl mx-auto px-4">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-12"
          >
            Your Orders
          </motion.h1>

          <div className="flex flex-col items-center justify-center py-24 text-center">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="w-32 h-32 rounded-full bg-gradient-to-br from-orange-500/20 to-orange-600/10 flex items-center justify-center mx-auto mb-8 border-2 border-orange-500/30"
            >
              <Package size={64} className="text-orange-500" />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-white text-2xl font-bold mb-4"
            >
              No orders yet
            </motion.h2>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-gray-400 text-lg mb-8 max-w-md"
            >
              Start shopping to place your first order!
            </motion.p>

            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/products')}
              className="px-8 py-4 bg-orange-500 hover:bg-orange-600 text-black font-bold text-lg rounded-lg transition-all"
            >
              Start Shopping
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#030804] via-[#050f0a] to-[#030804] pt-16 pb-10 font-[Arial]">
      <div className="max-w-6xl mx-auto px-3 sm:px-4">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl md:text-4xl font-bold text-white mb-5 md:mb-8"
        >
          Your Orders <span className="text-[#ffcc32]">({orders.length})</span>
        </motion.h1>

        <div className="space-y-3 md:space-y-4">
          {orders.map((order, index) => {
            const timelineSteps = getTimelineSteps(order);
            const statusMeta = getStatusMeta(order);
            const StatusIcon = statusMeta.Icon;
            const isExpanded = expandedOrder === order.id;
            const amountHidden = !!hiddenAmounts[order.id];
            const paymentStatus = formatStatusLabel(order.payment_status || order.payment_method || 'Paid');
            const estimatedDelivery =
              order.estimated_delivery_date
                ? formatDate(order.estimated_delivery_date)
                : order.estimated_delivery_days
                  ? `${order.estimated_delivery_days} days`
                  : 'Not available';
            const deliveryDate = order.delivery_date || order.delivered_at || order.delivered_date;

            return (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 18, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: index * 0.08, type: 'spring', stiffness: 120 }}
                className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.035] shadow-[0_18px_55px_rgba(0,0,0,0.28)] backdrop-blur-xl transition-colors hover:border-[#ffcc32]/30"
              >
                <div className="p-3 sm:p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-[#ffcc32]/30 bg-[#ffcc32]/10 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-[#ffcc32]">
                        <StatusIcon size={14} />
                        {statusMeta.label}
                      </div>
                      <h3 className="truncate text-base font-black text-white sm:text-lg md:text-xl">
                        Order #{order.id.substring(0, 8).toUpperCase()}
                      </h3>
                    </div>

                    <button
                      type="button"
                      onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                      className="shrink-0 rounded-full border border-white/10 bg-black/35 px-3 py-2 text-xs font-bold uppercase text-white transition hover:border-[#ffcc32]/40 hover:text-[#ffcc32]"
                    >
                      {isExpanded ? 'Hide Details' : 'View Details'}
                    </button>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2 lg:grid-cols-3">
                    <div className="rounded-xl border border-white/10 bg-black/35 p-2.5">
                      <p className="text-[10px] uppercase tracking-wide text-zinc-500">Payment Status</p>
                      <p className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-white">
                        <CreditCard size={14} className="text-[#ffcc32]" />
                        {paymentStatus}
                      </p>
                    </div>
                    <div className="col-span-2 rounded-xl border border-[#ffcc32]/20 bg-[#ffcc32]/[0.06] p-2.5 lg:col-span-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-[10px] uppercase tracking-wide text-zinc-500">Total Amount</p>
                          <p className="mt-1 truncate text-lg font-black text-[#ffcc32]">
                            {amountHidden ? 'Rs. ****' : rupee(order.total_amount)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleAmount(order.id)}
                          className="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-zinc-300 transition hover:border-[#ffcc32]/40 hover:text-[#ffcc32]"
                          aria-label={amountHidden ? 'Show amount' : 'Hide amount'}
                        >
                          {amountHidden ? <Eye size={15} /> : <EyeOff size={15} />}
                        </button>
                      </div>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-black/35 p-2.5">
                      <p className="text-[10px] uppercase tracking-wide text-zinc-500">Items</p>
                      <p className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-white">
                        <Package size={14} className="text-[#ffcc32]" />
                        {order.items?.length || 0} item{order.items?.length === 1 ? '' : 's'}
                      </p>
                    </div>
                  </div>
                </div>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.35, ease: 'easeInOut' }}
                      className="border-t border-white/5"
                    >
                      <div className="space-y-4 p-4">
                        {/* Order Summary */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between py-1.5 border-b border-white/5">
                            <span className="text-xs text-zinc-400">Payment</span>
                            <span className="text-xs font-semibold text-white">{paymentStatus}</span>
                          </div>
                          <div className="flex items-center justify-between py-1.5 border-b border-white/5">
                            <span className="text-xs text-zinc-400">Items</span>
                            <span className="text-xs font-semibold text-white">{order.items?.length || 0} item{order.items?.length === 1 ? '' : 's'}</span>
                          </div>
                          <div className="flex items-center justify-between py-1.5">
                            <span className="text-xs text-zinc-400">Total</span>
                            <span className="text-sm font-bold text-[#ffcc32]">{amountHidden ? 'Rs. ****' : rupee(order.total_amount)}</span>
                          </div>
                        </div>

                        {/* Order Progress */}
                        <div className="py-3">
                          <div className="mb-3">
                            <h3 className="text-sm font-bold text-white mb-0.5">Order Progress</h3>
                          </div>
                          
                          {order.order_status === 'cancelled' ? (
                            <div className="text-center py-4">
                              <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-red-500/10 mb-2">
                                <AlertCircle size={20} className="text-red-400" />
                              </div>
                              <p className="text-sm font-bold text-red-400 mb-1">Order Cancelled</p>
                              <p className="text-xs text-zinc-400">This order has been cancelled.</p>
                            </div>
                          ) : (
                            <>
                              <div className="relative mb-4">
                                <div className="overflow-x-auto pb-2">
                                  <div className="flex items-center justify-between min-w-[400px]">
                                    {timelineSteps.map((step, idx) => {
                                      const StepIcon = step.current ? StatusIcon : Check;
                                      const isLast = idx === timelineSteps.length - 1;
                                      
                                      return (
                                        <div key={step.key} className="flex-1 flex flex-col items-center">
                                          {!isLast && (
                                            <div className="absolute top-4 left-0 right-0 h-0.5 bg-white/10 -z-10">
                                              <div 
                                                className="h-full bg-[#ffcc32] transition-all duration-500"
                                                style={{ width: step.completed ? '100%' : '0%' }}
                                              />
                                            </div>
                                          )}
                                          <div
                                            className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full transition-all duration-300 ${
                                              step.current
                                                ? 'bg-[#ffcc32] text-black shadow-[0_0_16px_rgba(255,204,50,0.3)] scale-110'
                                                : step.completed
                                                  ? 'bg-[#ffcc32] text-black'
                                                  : 'bg-white/5 text-zinc-500'
                                            }`}
                                          >
                                            {step.completed || step.current ? (
                                              <StepIcon size={14} />
                                            ) : (
                                              <div className="h-1.5 w-1.5 rounded-full bg-zinc-600" />
                                            )}
                                          </div>
                                          <p className={`mt-2 text-[10px] font-medium tracking-wide ${step.active ? 'text-white' : 'text-zinc-500'}`}>
                                            {TIMELINE_LABELS[step.key] || step.label}
                                          </p>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>

                              <div className="bg-gradient-to-r from-[#ffcc32]/10 to-transparent border-l-2 border-[#ffcc32] pl-3 py-2">
                                <div className="flex items-center gap-2">
                                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#ffcc32]/20">
                                    <StatusIcon size={14} className="text-[#ffcc32]" />
                                  </div>
                                  <div>
                                    <p className="text-xs font-bold text-white">{statusMeta.label}</p>
                                    <p className="text-[10px] text-zinc-400 mt-0.5">{statusMeta.message}</p>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Shipment Details */}
                        <div className="py-3 border-t border-white/5">
                          <div className="mb-3">
                            <h3 className="text-sm font-bold text-white mb-0.5">Shipment Details</h3>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex items-start justify-between py-1.5 border-b border-white/5">
                              <span className="text-xs text-zinc-400">Courier</span>
                              <span className="text-xs font-medium text-white">{order.courier_name || 'Not available'}</span>
                            </div>
                            <div className="flex items-start justify-between py-1.5 border-b border-white/5">
                              <span className="text-xs text-zinc-400">AWB</span>
                              <span className="text-xs font-medium text-white">{order.awb_number || 'Not available'}</span>
                            </div>
                            <div className="flex items-start justify-between py-1.5 border-b border-white/5">
                              <span className="text-xs text-zinc-400">Tracking</span>
                              <div className="text-right">
                                {order.tracking_url ? (
                                  <a
                                    href={order.tracking_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs font-medium text-[#ffcc32] hover:text-[#ffda5c] transition-colors"
                                  >
                                    Track
                                  </a>
                                ) : (
                                  <span className="text-xs font-medium text-white">{order.tracking_id || 'Not available'}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-start justify-between py-1.5 border-b border-white/5">
                              <span className="text-xs text-zinc-400">Est. Delivery</span>
                              <span className="text-xs font-medium text-white">{estimatedDelivery}</span>
                            </div>
                            <div className="flex items-start justify-between py-1.5">
                              <span className="text-xs text-zinc-400">Delivery Date</span>
                              <span className="text-xs font-medium text-white">{deliveryDate ? formatDate(deliveryDate) : 'Not delivered yet'}</span>
                            </div>
                          </div>
                        </div>

                        {/* Delivery Address */}
                        {order.shipping_address && (
                          <div className="py-3 border-t border-white/5">
                            <div className="mb-3">
                              <h3 className="text-sm font-bold text-white mb-0.5">Delivery Address</h3>
                            </div>
                            
                            <div className="flex items-start gap-3">
                              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#ffcc32]/10">
                                <MapPin size={14} className="text-[#ffcc32]" />
                              </div>
                              <div className="flex-1">
                                <p className="text-xs leading-relaxed text-zinc-300">{formatAddress(order.shipping_address)}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Order Items */}
                        <div className="py-3 border-t border-white/5">
                          <div className="mb-3">
                            <h3 className="text-sm font-bold text-white mb-0.5">Order Items</h3>
                            <p className="text-xs text-zinc-400">{order.items?.length || 0} item{order.items?.length === 1 ? '' : 's'}</p>
                          </div>
                          
                          <div className="space-y-3">
                            {(order.items || []).map((item, itemIdx) => {
                              const lineTotal = Number(item.price || 0) * Number(item.quantity || 0);
                              const variants = [
                                item.size ? `Size ${item.size}` : null,
                                item.color ? `Color ${item.color}` : null,
                                item.weight ? `Weight ${item.weight} kg` : null,
                              ].filter(Boolean);

                              return (
                                <div
                                  key={itemIdx}
                                  className="flex gap-3 py-2 border-b border-white/5 last:border-0"
                                >
                                  <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg bg-[#0a1a0f]">
                                    <img
                                      src={getProductImage(item)}
                                      alt={item.name}
                                      loading="lazy"
                                      className="h-full w-full object-cover"
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-xs font-semibold text-white mb-1">{item.name}</h4>
                                    
                                    {variants.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mb-1">
                                        {variants.map((variant) => (
                                          <span key={variant} className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-white/5 text-zinc-300 border border-white/10">
                                            {variant}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    
                                    {item.item_type === 'combo' && item.selected_children && item.selected_children.length > 0 && (
                                      <div className="mb-1 text-[10px] text-zinc-400">
                                        {item.selected_children.map((child, childIdx) => (
                                          <span key={childIdx} className="mr-2">
                                            <span className="font-medium text-white">{child.name}</span>
                                            {child.size && <span className="text-zinc-500"> ({child.size})</span>}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    
                                    <p className="text-[10px] text-zinc-400">Qty: {item.quantity}</p>
                                  </div>
                                  <div className="text-right flex-shrink-0">
                                    <p className="text-sm font-bold text-[#ffcc32]">{rupee(lineTotal)}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="space-y-2 pt-3 border-t border-white/5">
                          <motion.button
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => generatePDF(order)}
                            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#ffcc32] px-4 py-3 text-xs font-bold uppercase text-black shadow-[0_4px_20px_rgba(255,204,50,0.3)] transition hover:bg-[#ffda5c] hover:shadow-[0_6px_25px_rgba(255,204,50,0.4)]"
                          >
                            <Download size={16} />
                            Download Invoice
                          </motion.button>

                          {canCustomerCancelOrder(order.order_status) && (
                            <>
                              <p className="text-center text-xs text-zinc-400">
                                You can cancel this order until admin confirms it.
                              </p>
                              <motion.button
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to cancel this order?')) {
                                    cancelOrder(order.id);
                                  }
                                }}
                                className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-red-500/50 px-4 py-3 text-xs font-bold text-red-400 transition hover:bg-red-500/10 hover:border-red-500"
                              >
                                <Clock size={16} />
                                Cancel Order
                              </motion.button>
                            </>
                          )}

                          {order.order_status === 'confirmed' && (
                            <p className="text-center text-xs text-zinc-400">
                              This order has been confirmed and can no longer be cancelled.
                            </p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}